<template>
  <div
    class="ea-chatbot right"
    :class="{ open: isOpen }"
    :style="styleVars"
    aria-live="polite"
  >
    <!-- Pestaña / botón -->
    <button class="ea-tab" @click="toggle" :aria-expanded="isOpen">
      <span v-if="lang==='ES'">Asistente</span>
      <span v-else>Assistant</span>
    </button>

    <!-- Panel del chat -->
    <section class="ea-panel" v-show="isOpen">
      <div class="ea-blob" aria-hidden="true"></div>
      <header class="ea-header">
        <div class="ea-title">
          <strong v-if="lang==='ES'">{{ brandName }} – Asistente</strong>
          <strong v-else>{{ brandName }} – Assistant</strong>
          <small>{{ company.name }}</small>
        </div>
        <button class="ea-close" @click="isOpen=false" :aria-label="lang==='ES' ? 'Cerrar' : 'Close'">×</button>
      </header>

      <!-- Sugerencias -->
      <div class="ea-suggest">
        <button
          v-for="(s, i) in suggestions"
          :key="i"
          class="chip"
          @click="quickAsk(s)"
          :title="s"
        >{{ s }}</button>
      </div>

      <div class="ea-body" ref="scrollBox">
        <div v-for="(m,idx) in messages" :key="idx" class="ea-msg" :class="m.role">
          <div class="ea-bubble" v-html="m.html"></div>
        </div>
        <div v-if="typing" class="ea-msg bot">
          <div class="ea-bubble">
            <span class="dots"><i></i><i></i><i></i></span>
          </div>
        </div>
      </div>

      <!-- FAQs -->
      <div class="ea-faqs">
        <button v-for="(q, i) in faqs" :key="'faq-'+i" class="faq" @click="quickAsk(q)">{{ q }}</button>
      </div>

      <footer class="ea-footer">
        <input
          v-model="draft"
          :placeholder="lang==='ES' ? 'Escribe tu pregunta...' : 'Type your question...'"
          @keydown.enter.prevent="handleSend"
          class="ea-input"
        />
        <button class="ea-send" @click="handleSend" :aria-label="lang==='ES' ? 'Enviar' : 'Send'">➤</button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ChatBotEA',
  props: {
    lang: { type: String, default: 'ES' }, // 'ES' | 'EN'
    brandName: { type: String, default: 'Latindreamer' },
    autoOpen: { type: Boolean, default: false }, // no auto-abrir
    theme: {
      type: Object,
      default: () => ({
        primary: '#7a2bff',
        secondary: '#0ea5e9',
        accent: '#eab308',
        dark: '#0f172a'
      })
    },
    company: {
      type: Object,
      default: () => ({
        name: 'Latindreamer',
        phone: '(612) 600-5144',
        phoneHref: 'tel:+16126005144',
        email: 'hola@latindreamer.com',
        address: 'Minneapolis, MN',
        maps: 'https://maps.google.com/?q=Minneapolis,MN',
        wa: 'https://wa.me/16126005144?text=Hola,%20quiero%20impulsar%20mi%20negocio%20con%20Web%20%2B%20IA'
      })
    },
    socials: {
      type: Object,
      default: () => ({
        facebook: 'https://facebook.com/',
        tiktok: 'https://tiktok.com/@'
      })
    },
    // Servicios Latindreamer
    services: {
      type: Array,
      default: () => ([
        'Sitio web que convierte (Landing / Web corporativa)',
        'Asistente con IA (web / WhatsApp / Facebook)',
        'Meta Ads (Facebook/Instagram) para conseguir prospectos',
        'Google Ads para búsquedas locales',
        'SEO técnico + Contenidos',
        'Automatizaciones y CRM (embudos + email/WhatsApp)',
        'Analítica y reportes claros',
        'Mantenimiento y soporte'
      ])
    },
    hours: {
      type: Object,
      default: () => ({
        ES: 'Lun–Sáb 9:00 a 18:00 (CST).',
        EN: 'Mon–Sat 9:00 AM – 6:00 PM (CST).'
      })
    },
    coverage: {
      type: Object,
      default: () => ({
        ES: 'Trabajamos remoto en USA y local en Minnesota.',
        EN: 'Remote across the U.S., local in Minnesota.'
      })
    }
  },
  data() {
    return {
      isOpen: this.autoOpen,
      hasWelcomed: false,
      draft: '',
      messages: [],
      typing: false
    }
  },
  computed: {
    styleVars(){
      return {
        '--ea-primary': this.theme.primary,
        '--ea-secondary': this.theme.secondary,
        '--ea-accent': this.theme.accent,
        '--ea-dark': this.theme.dark
      }
    },
    suggestions(){
      return this.lang==='ES'
        ? [
            'Quiero una página chida',
            'Poner un bot con IA en WhatsApp',
            'Quiero clientes con Meta Ads',
            'Google Ads para mi zona',
            '¿Cuánto cobran?',
            'Ver portafolio'
          ]
        : [
            'I need a solid website',
            'AI bot on WhatsApp',
            'Get leads with Meta Ads',
            'Google Ads near me',
            'How much is it?',
            'See portfolio'
          ];
    },
    faqs(){
      return this.lang==='ES'
        ? [
            '¿Qué trae el combo Web + IA?',
            'Explícame rápido los embudos',
            '¿Qué resultados puedo esperar?',
            '¿El gasto en anuncios va aparte?',
            '¿Ustedes hacen el texto y diseño?',
            '¿Usan CRM y automatizaciones?',
            '¿En cuánto tiempo lo tienen?',
            '¿Hay soporte o garantía?',
            '¿Cómo miden los resultados?',
            '¿Trabajan mi giro?'
          ]
        : [
            'What’s in Web + AI?',
            'Funnels, quick version',
            'What results can I expect?',
            'Is ad spend separate?',
            'Do you handle copy & design?',
            'CRM and automations?',
            'How long does it take?',
            'Support or warranty?',
            'How do you track results?',
            'Do you work my niche?'
          ]
    }
  },
  methods: {
    toggle(){
      const opening = !this.isOpen
      this.isOpen = opening
      if (opening && !this.hasWelcomed) {
        const helloES = `✨ ¡Hola! Soy el asistente de <strong>${this.brandName}</strong>.<br>
Hacemos <em>webs que venden</em>, <em>IA que contesta 24/7</em> y <em>anuncios que traen prospectos</em>.<br>
Cuéntame qué necesitas y te ayudo sin rodeos.`;
        const helloEN = `✨ Hey! I’m <strong>${this.brandName}</strong>’s assistant.<br>
We build <em>sites that sell</em>, <em>24/7 AI chat</em>, and <em>ads that bring leads</em>.<br>
Tell me what you need and I’ll keep it simple.`;
        this.pushBot(this.lang==='ES' ? helloES : helloEN);
        this.hasWelcomed = true
      }
      this.$nextTick(this.scrollToEnd)
    },
    scrollToEnd(){
      const el = this.$refs.scrollBox;
      if (el) el.scrollTop = el.scrollHeight;
    },
    quickAsk(text){
      this.draft = text;
      this.handleSend();
    },
    handleSend() {
      const text = this.draft.trim();
      if (!text) return;
      this.pushUser(this.escape(text));
      this.draft = '';
      this.typing = true;
      setTimeout(() => {
        const replyHtml = this.route(text);
        this.pushBot(replyHtml);
        this.typing = false;
        this.$nextTick(this.scrollToEnd);
      }, 420);
    },
    pushUser(html){ this.messages.push({ role: 'user', html }) ; this.$nextTick(this.scrollToEnd) },
    pushBot(html){ this.messages.push({ role: 'bot', html }) ; this.$nextTick(this.scrollToEnd) },
    escape(s){ return s.replace(/[<>&"]/g, ch => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[ch])) },

    // --- Router por palabras clave (Web + IA + Ads + SEO) con tono coloquial ---
    route(qRaw){
      const q = qRaw.toLowerCase();
      const ES = this.lang === 'ES';

      const linkPhone = `<a href="${this.company.phoneHref}" class="ea-inline">${this.company.phone}</a>`;
      const linkMail  = `<a href="mailto:${this.company.email}" class="ea-inline">${this.company.email}</a>`;
      const linkMsg   = `<a href="${this.company.wa}" target="_blank" class="ea-btn">WhatsApp</a>`;
      const linkMaps  = `<a href="${this.company.maps}" target="_blank" class="ea-inline">Google Maps</a>`;

      const fb = this.socials.facebook ? `<a href="${this.socials.facebook}" class="ea-inline" target="_blank">Facebook</a>` : '';
      const tk = this.socials.tiktok   ? `<a href="${this.socials.tiktok}" class="ea-inline" target="_blank">TikTok</a>` : '';

      // Servicios / Qué hacemos
      if (/(servicio|servicios|qué hacen|que hacen|service|services|what do you do)/.test(q)) {
        const list = this.services.map(s => `<li>${this.escape(s)}</li>`).join('');
        return ES
          ? `<p>Esto es lo que hacemos, sin choro:</p><ul>${list}</ul><p>¿Lo vemos por Whats? ${linkMsg}</p>`
          : `<p>Here’s what we do, straight up:</p><ul>${list}</ul><p>Wanna chat on WhatsApp? ${linkMsg}</p>`;
      }

      // Sitio web
      if (/(sitio|web|p[aá]gina|website|landing|tienda|e[- ]?commerce)/.test(q)) {
        return ES
          ? `<p><strong>Web que vende</strong>: diseño con intención, textos que conectan, carga rápida, SEO técnico y formulario/WhatsApp directo al CRM. Todo medido, cero humo.</p><p>¿Te enseño una demo o cotización express? ${linkMsg}</p>`
          : `<p><strong>Website that sells</strong>: intent-first design, punchy copy, fast load, technical SEO and form/WhatsApp to your CRM. Tracked end-to-end.</p><p>Demo or quick quote? ${linkMsg}</p>`;
      }

      // IA / Chatbot / WhatsApp / Facebook
      if (/(ia|ai|chat(bot)?|whats ?app|facebook|instagram|messenger|contestador|automa)/.test(q)) {
        return ES
          ? `<p><strong>Bot con IA</strong>: responde 24/7 en tu web y redes, con tus respuestas y tu estilo. Captura datos y dispara seguimientos. </p><p>¿Lo encendemos? ${linkMsg}</p>`
          : `<p><strong>AI bot</strong>: answers 24/7 on site & socials, in your tone. Captures data and triggers follow-ups. CRM-ready.</p><p>Wanna switch it on? ${linkMsg}</p>`;
      }

 // --- Seguimiento automático / CRM / Emails (versión simple y coloquial)
if (/(embudo|funnel|crm|pipe|automatiza|automation|email|newsletter|follow|seguimiento)/.test(q)) {
  return ES
    ? `<p><strong>Seguimiento automático</strong>: cuando alguien te escribe o deja sus datos,
se le responde solo, se guarda en tu lista y se le manda info paso a paso hasta que decide comprar.
Así no pierdes clientes por falta de seguimiento.</p>
<p>¿Quieres que revisemos cómo lo tienes y lo mejoremos? ${linkMsg}</p>`
    : `<p><strong>Automatic follow-up</strong>: when someone writes you or leaves their info,
they get an instant reply, they’re saved in your list, and they get follow-up messages until they’re ready to buy.
That way you don’t lose leads for lack of follow-up.</p>
<p>Want me to check your current setup and improve it? ${linkMsg}</p>`;
}

      // Meta Ads
      if (/(meta|facebook|instagram|ads|anuncio|camp[aá]ñas?).*(meta|facebook|instagram|ads)?/.test(q)) {
        return ES
          ? `<p><strong>Meta Ads</strong>: creativos que llaman, audiencias bien armadas y landing que convierte. </p><p>¿Vemos inversión y metas realistas? ${linkMsg}</p>`
          : `<p><strong>Meta Ads</strong>: thumb-stopping creatives, right audiences, and a landing that converts. Pixel/events set right and clean reporting.</p><p>Check spend & realistic goals? ${linkMsg}</p>`;
      }
// Google Ads
if (/(google|search|b[uú]squeda|sem|ppc).*(ads|anuncio|campaign)?/.test(q)) {
  return ES
    ? `<p><strong>Google Ads</strong>: hacemos que tu negocio aparezca arriba en Google justo cuando la gente está buscando lo que ofreces. 
Pagas solo por clics de personas interesadas y medimos cuántos se convierten en clientes.</p>
<p>¿Quieres que revisemos tu zona y veas cuántos clientes puedes ganar? ${linkMsg}</p>`
    : `<p><strong>Google Ads</strong>: we make your business show up on top of Google right when people are searching for what you offer. 
You only pay for clicks from interested people and we track how many turn into customers.</p>
<p>Want me to check your area and show how many clients you can get? ${linkMsg}</p>`;
}

// SEO
if (/\bseo\b|posicionamiento|org[aá]nico|contenido|blog|linkbuild/.test(q)) {
  return ES
    ? `<p><strong>SEO</strong>: hacemos que tu negocio salga en Google sin pagar anuncios. 
Optimizamos tu web y creamos contenido para que la gente te encuentre fácil cuando busca lo que vendes. 
Lo importante no son las visitas, sino los clientes que llegan.</p>
<p>¿Quieres que te haga una revisión rápida de tu página? ${linkMsg}</p>`
    : `<p><strong>SEO</strong>: we help your business show up on Google without paying for ads. 
We optimize your website and create content so people can easily find you when they search for what you sell. 
What matters isn’t traffic, but the customers that come in.</p>
<p>Want me to do a quick review of your site? ${linkMsg}</p>`;
}


      // Portafolio / casos
      if (/(portafolio|portfolio|casos|trabajos|resultados|demos?)/.test(q)) {
        const linksES = `
          <ul>
            <li><a class="ea-inline" href="https://ea-magic.com" target="_blank">EA Magic (Servicios)</a></li>
            <li><a class="ea-inline" href="https://myevento-digital.com" target="_blank">My Evento Digital</a></li>
            <li><a class="ea-inline" href="https://misscoquette.com.mx" target="_blank">Miss Coquette</a></li>
          </ul>`;
        return ES
          ? `<p>Checa algunos trabajos:</p>${linksES}<p>¿Quieres una demo aterrizada a tu giro? ${linkMsg}</p>`
          : `<p>Here are a few:</p>${linksES}<p>Want a demo tailored to your niche? ${linkMsg}</p>`;
      }

      // Precios / paquetes
      if (/(precio|precios|paquete|paquetes|plan|planes|costo|quote|pricing|how much)/.test(q)) {
        return ES
          ? `<p>Manejamos <strong>paquetes</strong> (Web + IA) y <strong>mensualidades</strong> (Ads/SEO).El presupuesto depende del alcance. Te cotizo rápido por  con 3-4 preguntitas.</p><p>${linkMsg} · o llámanos ${linkPhone}</p>`
          : `<p>We do <strong>bundles</strong> (Web + AI) and <strong>monthly</strong> (Ads/SEO). Price depends on scope. Quick WhatsApp quote with 3-4 questions.</p><p>${linkMsg} · or call ${linkPhone}</p>`;
      }

      // Tiempos
      if (/(tiempo|entrega|timeline|deadline|cu[aá]nto tarda|when ready)/.test(q)) {
        return ES
          ? `<p>Tiempo típico: Landing 1–2 semanas; Web + IA 2–4; campañas 3–7 días. Depende de tu feedback y materiales (logo, fotos, textos).</p><p>¿Agendamos kick-off? ${linkMsg}</p>`
          : `<p>Typical: Landing 1–2 weeks; Web + AI 2–4; campaigns 3–7 days. Depends on feedback and assets.</p><p>Kick-off? ${linkMsg}</p>`;
      }

      // Analítica / medición
      if (/(medici[óo]n|analytics|anal[íi]tica|kpi|reporte|tracking|pixel|gtm|ga4|events?)/.test(q)) {
        return ES
          ? `<p>Montamos GA4/GTM, píxeles/eventos y tableros sencillos: leads, CPL, CTR, CPA/ROAS, llamadas y chats. Números claros, sin rollo.</p><p>¿Qué KPI te importa más? ${linkMsg}</p>`
          : `<p>GA4/GTM, pixels/events and simple dashboards: leads, CPL, CTR, CPA/ROAS, calls/chats. Clear numbers.</p><p>Which KPI matters most? ${linkMsg}</p>`;
      }

      // Soporte / garantía
      if (/(soporte|garant[íi]a|warranty|support|mantenimiento|bugs?|sla)/.test(q)) {
        return ES
          ? `<p>Incluimos soporte de arranque y podemos llevarte el mantenimiento mensual. Si algo no queda como se definió, lo arreglamos.</p>`
          : `<p>Launch support included; we can handle monthly maintenance. If it’s not as scoped, we fix it.</p>`;
      }

      // Cobertura / ubicación
      if (/(zona|cobertura|área|coverage|where|location|minnesota|usa|remote|direcci[óo]n|address)/.test(q)) {
        return ES
          ? `<p>${this.coverage.ES} Ubicación: ${linkMaps}</p>`
          : `<p>${this.coverage.EN} Location: ${linkMaps}</p>`;
      }

      // Horarios
      if (/(horario|horarios|hours|open|opening|schedule)/.test(q)) {
        return ES ? `<p>${this.hours.ES}</p>` : `<p>${this.hours.EN}</p>`;
      }

      // Contacto
      if (/(contact|contacto|phone|tel|email|mail|llamar|call|whats)/.test(q)) {
        return ES
          ? `<p>Contacto directo: ${linkPhone} · ${linkMail} · ${linkMsg}</p>`
          : `<p>Reach us: ${linkPhone} · ${linkMail} · ${linkMsg}</p>`;
      }

      // Redes
      if (/(red(es)?|social(es)?|facebook|tik ?tok|tiktok|instagram)/.test(q)) {
        const none = ES ? 'Pronto compartimos más redes.' : 'More socials soon.';
        const parts = [fb, tk].filter(Boolean).join(' · ');
        return ES ? `<p>Échanos follow: ${parts || none}</p>` : `<p>Follow us: ${parts || none}</p>`;
      }

      // Fallback
      return ES
        ? `<p>Puedo ayudarte con <em>Web, IA, Ads, SEO, embudos, analítica y precios</em>. Dime tu meta y lo armamos.</p>`
        : `<p>I can help with <em>Web, AI, Ads, SEO, funnels, analytics and pricing</em>. Tell me your goal and we’ll plan it.</p>`;
    }
  }
}
</script>

<style scoped>
/* ====== Layout base, lado derecho ====== */
.ea-chatbot { position: fixed; bottom: 24px; z-index: 9999; }
.ea-chatbot.right { right: 0; }
.ea-chatbot.open .ea-tab { filter: brightness(.95); }

.ea-tab {
  position: absolute; right: 8px; bottom: 0;
  background: linear-gradient(135deg, var(--ea-secondary), var(--ea-primary) 60%, var(--ea-accent));
  color: #fff; border: 0;
  padding: 10px 14px; border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0,0,0,.25);
  cursor: pointer; font-weight: 700; letter-spacing:.2px;
  backdrop-filter: blur(6px);
  transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
}
.ea-tab:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(0,0,0,.3); }

.ea-panel {
  width: min(360px, 92vw); height: 520px;
  background: rgba(255,255,255,.9); border-radius: 16px;
  border: 1px solid rgba(255,255,255,.5);
  box-shadow: 0 30px 60px rgba(2,6,23,.30), 0 0 0 1px rgba(14,23,42,.04) inset;
  overflow: hidden; margin-right: 8px; margin-bottom: 50px;
  display: flex; flex-direction: column;
  backdrop-filter: blur(12px);
  animation: pop .45s ease both;
  position: relative;
}

/* Fondo moderno (blob) */
.ea-blob {
  position: absolute; inset: -40% -30% auto auto; height: 260px; width: 260px;
  background: radial-gradient(closest-side, color-mix(in srgb, var(--ea-primary) 35%, transparent), color-mix(in srgb, var(--ea-secondary) 25%, transparent), transparent 70%);
  filter: blur(18px);
  animation: float 6s ease-in-out infinite alternate;
  pointer-events: none;
}

/* Header */
.ea-header {
  padding: 12px 14px; background: linear-gradient(135deg, var(--ea-dark), #111827);
  color: #fff; display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.ea-title small { display:block; opacity:.8; font-weight:400; }
.ea-close { background: transparent; color: #fff; border: 0; font-size: 22px; cursor: pointer; }

/* Sugerencias */
.ea-suggest { display:flex; flex-wrap: wrap; gap: 8px; padding: 10px 12px 0; }
.chip {
  border:1px solid rgba(2,6,23,.12); border-radius: 999px; padding:6px 10px;
  background: rgba(255,255,255,.7); backdrop-filter: blur(8px);
  font-size: 12px; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease;
}
.chip:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }

/* Body */
.ea-body {
  flex: 1; padding: 12px; overflow: auto; background: linear-gradient(180deg, #f8fafc, #f1f5f9);
}

/* FAQs */
.ea-faqs { display:flex; gap: 6px; padding: 6px 12px; overflow-x:auto; }
.faq {
  white-space: nowrap; font-size: 12px; padding: 6px 10px; border-radius: 10px;
  background:#0f172a; color:#fff; border:0; cursor:pointer;
  opacity:.95; transition: opacity .2s ease, transform .2s ease;
}
.faq:hover { opacity:1; transform: translateY(-1px); }

/* Footer */
.ea-footer { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e2e8f0; background: rgba(255,255,255,.8); }
.ea-input {
  flex: 1; border: 1px solid #cbd5e1; border-radius: 12px; padding: 11px 12px;
}
.ea-send {
  background: linear-gradient(135deg, var(--ea-secondary), var(--ea-primary));
  color: #fff; border: 0; border-radius: 12px; padding: 0 14px; cursor: pointer; font-weight:700;
  box-shadow: 0 8px 18px color-mix(in srgb, var(--ea-primary) 35%, transparent);
}

/* Mensajes */
.ea-msg { display:flex; margin: 8px 0; }
.ea-msg.user { justify-content: flex-end; }
.ea-bubble {
  max-width: 85%; padding: 10px 12px; border-radius: 12px; line-height: 1.45;
  background: #fff; border: 1px solid #e2e8f0;
  animation: fadeIn .2s ease both;
}
.ea-msg.user .ea-bubble {
  background: linear-gradient(135deg, var(--ea-secondary), var(--ea-primary));
  color: #fff; border-color: transparent;
}
.ea-inline { color: var(--ea-secondary); text-decoration: underline; }
.ea-btn {
  display:inline-block; padding: 6px 10px; border-radius: 10px; border:1px solid var(--ea-dark);
  text-decoration:none; color:var(--ea-dark); font-weight:700; background:#fff;
}

/* Typing dots */
.dots { display:inline-flex; gap:4px; }
.dots i { width:6px; height:6px; border-radius:50%; background:#94a3b8; display:block; animation: b 1s infinite ease-in-out; }
.dots i:nth-child(2){ animation-delay: .15s } .dots i:nth-child(3){ animation-delay: .3s }

/* Animaciones */
@keyframes b { 0%,80%,100%{ transform: translateY(0)} 40%{ transform: translateY(-4px)} }
@keyframes pop { from{ transform: translateY(10px) scale(.98); opacity:0 } to{ transform: translateY(0) scale(1); opacity:1 } }
@keyframes float { from{ transform: translate(0,0)} to{ transform: translate(-10px,10px)} }

@media (max-width: 480px){
  .ea-panel { height: 70vh; }
}
</style>
